import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Eye, EyeOff, ShieldCheck, Copy, Check, ArrowLeft, AlertCircle,
  Clock3, ScrollText,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Field, Alert, Card } from '../components/ui/primitives';
import cn from '../lib/cn';
import Wordmark from '../components/Wordmark';

/**
 * Sign-in, in up to three steps.
 *
 *   password → [enrol 2FA on a first login] → [verify 2FA] → console
 *
 * Enrolment is part of signing in rather than something to do later: 2FA is
 * mandatory, so a session issued before enrolment would be one the API refuses.
 *
 * ── On the layout ───────────────────────────────────────────────────────────
 * A split: the door on the right, what is behind it on the left. The left panel
 * is not decoration — it states the three constraints this console actually
 * enforces, so the first thing an operator learns is that the session is short,
 * watched and recorded. It collapses away below `lg`, where a form on a phone
 * should be the only thing on screen.
 *
 * The one deliberately expressive element is the slow sweep behind that panel.
 * A console for vehicle diagnostics earns a diagnostic sweep; it runs at very
 * low alpha, once every nine seconds, and stops entirely under
 * prefers-reduced-motion.
 */

/**
 * Every line here is a property the code really has — the short TTL in
 * config/env.js, the mandatory TOTP, the audit router that exposes no write
 * routes. Sign-in screens are where reassurance is cheapest to fake, so nothing
 * on this list is aspirational.
 */
const ASSURANCES = [
  {
    icon: ShieldCheck,
    title: 'Two-factor, always',
    body: 'TOTP is mandatory on every account. A password alone will not open this console.',
  },
  {
    icon: Clock3,
    title: 'Eight-hour sessions',
    body: 'Short on purpose. Sessions can be revoked centrally the moment an account is in doubt.',
  },
  {
    icon: ScrollText,
    title: 'Every action recorded',
    body: 'The audit trail is append-only — it exposes no write routes, not even to a superadmin.',
  },
];

export default function Login() {
  const {
    isAuthenticated, bootstrapping, login, verifyMfa, enrollMfa, pending,
    cancelPending, sessionMessage,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const [step, setStep] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (pending?.stage === 'verify') setStep('verify');
    else if (pending?.stage === 'enroll') setStep('enroll');
  }, [pending]);

  const destination = location.state?.from?.pathname || '/';

  if (!bootstrapping && isAuthenticated && !recoveryCodes) {
    return <Navigate to={destination} replace />;
  }

  const submitPassword = async (event) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await login(email.trim(), password);
      if (result.stage === 'done') navigate(destination, { replace: true });
      else setStep(result.stage);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (event) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (step === 'enroll') {
        // Recovery codes come back once and are hashed at rest, so this screen
        // is the only chance to record them.
        setRecoveryCodes(await enrollMfa(code.trim()));
      } else {
        await verifyMfa(code.trim());
        navigate(destination, { replace: true });
      }
    } catch (err) {
      setError(err.message);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    cancelPending();
    setStep('password');
    setCode('');
    setPassword('');
    setError(null);
  };

  const copyCodes = async () => {
    await navigator.clipboard?.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // A wrong password because Caps Lock was down is indistinguishable from a
  // wrong password, and this form only allows ten of those per 15 minutes.
  const trackCapsLock = (event) => {
    if (typeof event.getModifierState === 'function') {
      setCapsLock(event.getModifierState('CapsLock'));
    }
  };

  const rise = (delay = 0) => (reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 } }
    : {
      initial: { opacity: 0, y: 14 },
      animate: { opacity: 1, y: 0 },
      transition: { delay, duration: 0.5, ease: [0.32, 0.72, 0, 1] },
    });

  // Slide direction carries meaning: forward through the steps, back on restart.
  const stepMotion = (from) => (reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
      initial: { opacity: 0, x: from },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -from },
      transition: { duration: 0.28, ease: [0.32, 0.72, 0, 1] },
    });

  return (
    <div className="relative min-h-screen overflow-hidden bg-canvas">
      {/* ── Ambient ──────────────────────────────────────────────────────────
          A hairline grid, radially masked so it never reaches an edge as a
          hard line, and a single slow sweep across it. Both sit at an alpha
          low enough to survive the light appearance, where a heavier treatment
          would read as noise rather than depth. */}
      <div
        aria-hidden
        /**
         * Ink, not a semantic token. `--separator` is tuned to sit between two
         * surfaces, and in the dark appearance it is 56,56,58 — against a pure
         * black canvas that is invisible at any alpha low enough to stay
         * ambient. A grid wants plain ink instead: black in light, white in
         * dark, both at a few percent.
         *
         * 1px rather than the system's 0.5px hairline for the same reason. A
         * half-pixel line on a 1x display is drawn at half alpha, which on top
         * of an already low alpha leaves nothing.
         */
        className={cn(
          'pointer-events-none absolute inset-0',
          '[--grid-c:0_0_0] [--grid-a:0.055]',
          'dark:[--grid-c:255_255_255] dark:[--grid-a:0.05]',
        )}
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--grid-c) / var(--grid-a)) 1px, transparent 1px),'
            + 'linear-gradient(90deg, rgb(var(--grid-c) / var(--grid-a)) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          // Kept clear of every edge, so the grid never terminates on a
          // straight line the eye can find.
          maskImage: 'radial-gradient(ellipse 62% 52% at 26% 42%, #000 15%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(ellipse 62% 52% at 26% 42%, #000 15%, transparent 78%)',
        }}
      />
      {!reduceMotion && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 h-[45vh] [--sweep-a:0.07] dark:[--sweep-a:0.16]"
          style={{
            background:
              'linear-gradient(180deg, transparent, rgb(var(--blue) / var(--sweep-a)), transparent)',
          }}
          initial={{ y: '-50vh' }}
          animate={{ y: '105vh' }}
          transition={{ duration: 9, repeat: Infinity, ease: 'linear', repeatDelay: 1.5 }}
        />
      )}

      <div className="relative mx-auto flex min-h-screen max-w-5xl items-center px-6 py-10">
        <div className="grid w-full items-center gap-14 lg:grid-cols-[1fr_380px]">

          {/* ── Context panel ────────────────────────────────────────────── */}
          <div className="hidden lg:block">
            {/* The wordmark carries the name, so repeating "JobCard" as text
                beside it would only say it twice. */}
            <motion.div {...rise(0.05)}>
              <Wordmark height={30} />
              <p className="mt-2.5 text-footnote text-label3">Platform Console</p>
            </motion.div>

            <motion.h1
              {...rise(0.14)}
              className="mt-10 max-w-[20ch] text-balance text-largetitle font-semibold text-label"
            >
              Every garage on the platform. One console.
            </motion.h1>

            <motion.p
              {...rise(0.2)}
              className="mt-4 max-w-[46ch] text-subhead leading-relaxed text-label2"
            >
              Tenants, users, job cards and the platform’s own health — read from the
              live database that the garage app writes to.
            </motion.p>

            <div className="mt-12 space-y-7">
              {ASSURANCES.map(({ icon: Icon, title, body }, i) => (
                <motion.div key={title} {...rise(0.3 + i * 0.09)} className="flex gap-3.5">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-field bg-blue/10">
                    <Icon className="h-3.5 w-3.5 text-blue" />
                  </div>
                  <div>
                    <p className="text-subhead font-medium text-label">{title}</p>
                    <p className="mt-1 max-w-[42ch] text-footnote leading-relaxed text-label3">
                      {body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ── The door ─────────────────────────────────────────────────── */}
          <motion.div
            className="mx-auto w-full max-w-[380px]"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.985 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            transition={reduceMotion
              ? { duration: 0.2 }
              : { type: 'spring', stiffness: 340, damping: 32, delay: 0.08 }}
          >
            {/* The identity block the context panel already carries, restored
                for the narrow layout where that panel is gone. */}
            <div className="mb-7 flex flex-col items-center gap-2.5 lg:hidden">
              <Wordmark height={28} />
              <p className="text-footnote text-label3">Platform Console</p>
            </div>

            <Card className="overflow-hidden shadow-raised">
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {/* ── Recovery codes ───────────────────────────────────── */}
                  {recoveryCodes ? (
                    <motion.div key="recovery" {...stepMotion(20)} className="space-y-4">
                      <div>
                        <h2 className="text-title3 font-semibold text-label">
                          Save your recovery codes
                        </h2>
                        <p className="mt-1 text-footnote leading-relaxed text-label3">
                          Each works once, and they are the only way back in if you lose
                          your authenticator. They are stored hashed — this screen is the
                          only time they can be shown.
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-1.5 rounded-field bg-fill/[0.06] p-3 ring-1 ring-inset ring-separator">
                        {recoveryCodes.map((rc, i) => (
                          <motion.span
                            key={rc}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: reduceMotion ? 0 : i * 0.04 }}
                            className="select-all font-mono text-footnote text-blue-300"
                          >
                            {rc}
                          </motion.span>
                        ))}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyCodes}
                        icon={copied
                          ? <Check className="h-3.5 w-3.5 text-green-400" />
                          : <Copy className="h-3.5 w-3.5" />}
                        className="w-full"
                      >
                        {copied ? 'Copied to clipboard' : 'Copy all codes'}
                      </Button>

                      <Button
                        variant="primary"
                        size="lg"
                        className="w-full"
                        onClick={() => navigate(destination, { replace: true })}
                      >
                        I have saved them — continue
                      </Button>
                    </motion.div>
                  ) : step === 'password' ? (
                    /* ── Password ─────────────────────────────────────────── */
                    <motion.form
                      key="password"
                      onSubmit={submitPassword}
                      {...stepMotion(-20)}
                      className="space-y-4"
                    >
                      <div>
                        <h2 className="text-title3 font-semibold text-label">Sign in</h2>
                        <p className="mt-1 text-footnote text-label3">
                          Platform accounts only — superadmins and field supervisors.
                        </p>
                      </div>

                      {sessionMessage && (
                        <Alert tone="info" icon={<AlertCircle className="h-4 w-4" />}>
                          {sessionMessage}
                        </Alert>
                      )}
                      {error && (
                        <Alert tone="red" icon={<AlertCircle className="h-4 w-4" />}>
                          {error}
                        </Alert>
                      )}

                      <Field label="Email" htmlFor="email">
                        <Input
                          id="email"
                          type="email"
                          required
                          autoFocus
                          autoComplete="username"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                        />
                      </Field>

                      <Field label="Password" htmlFor="password">
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={trackCapsLock}
                            onKeyUp={trackCapsLock}
                            onBlur={() => setCapsLock(false)}
                            className="pr-9"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-label4 transition-colors hover:text-label2"
                          >
                            {showPassword
                              ? <EyeOff className="h-3.5 w-3.5" />
                              : <Eye className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                        <AnimatePresence>
                          {capsLock && (
                            <motion.p
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.18 }}
                              className="flex items-center gap-1.5 text-caption2 text-orange"
                            >
                              <AlertCircle className="h-3 w-3 shrink-0" />
                              Caps Lock is on
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </Field>

                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        loading={busy}
                        disabled={!email || !password}
                      >
                        {busy ? 'Checking' : 'Continue'}
                      </Button>
                    </motion.form>
                  ) : (
                    /* ── Enrol or verify ──────────────────────────────────── */
                    <motion.form
                      key="mfa"
                      onSubmit={submitCode}
                      {...stepMotion(20)}
                      className="space-y-4"
                    >
                      <div>
                        <h2 className="text-title3 font-semibold text-label">
                          {step === 'enroll' ? 'Set up two-factor' : 'Two-factor authentication'}
                        </h2>
                        <p className="mt-1 text-footnote leading-relaxed text-label3">
                          {step === 'enroll'
                            ? 'This console can read every garage’s customer records, so a second factor is required before it will admit you.'
                            : `Enter the 6-digit code for ${pending?.email}.`}
                        </p>
                      </div>

                      {error && (
                        <Alert tone="red" icon={<AlertCircle className="h-4 w-4" />}>
                          {error}
                        </Alert>
                      )}

                      {step === 'enroll' && (
                        <div className="space-y-3">
                          <div className="flex justify-center">
                            {pending?.qrDataUrl && (
                              <motion.img
                                initial={{ opacity: 0, scale: 0.94 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: reduceMotion ? 0 : 0.1 }}
                                src={pending.qrDataUrl}
                                alt="Scan this QR code with your authenticator app"
                                className="h-44 w-44 rounded-field bg-white p-1.5 ring-1 ring-inset ring-separator"
                              />
                            )}
                          </div>
                          <p className="text-center text-caption2 text-label4">
                            Scan with Google Authenticator, 1Password, Authy…
                          </p>

                          <div className="flex items-center gap-2">
                            <div className="h-px flex-1 bg-separator" />
                            <span className="text-caption2 uppercase tracking-wide text-separator2">
                              or key in manually
                            </span>
                            <div className="h-px flex-1 bg-separator" />
                          </div>

                          <p className="select-all break-all rounded bg-fill/[0.06] px-3 py-2 text-center font-mono text-footnote text-teal-300 ring-1 ring-inset ring-separator">
                            {pending?.secret}
                          </p>
                        </div>
                      )}

                      <Field
                        label={step === 'enroll' ? 'Code from your app' : 'Code or recovery code'}
                        htmlFor="code"
                      >
                        <Input
                          id="code"
                          required
                          autoFocus
                          value={code}
                          onChange={(e) => setCode(e.target.value)}
                          placeholder="000000"
                          // Not type=number: recovery codes are alphanumeric, and a
                          // spinner on a one-time code is nonsense either way.
                          inputMode="text"
                          autoComplete="one-time-code"
                          spellCheck={false}
                          className={cn('text-center font-mono text-body tracking-[0.3em]')}
                        />
                        {/* Fills as the six digits arrive, and simply stays full
                            for a longer recovery code — no assumption about length. */}
                        <div className="h-[2px] overflow-hidden rounded-full bg-fill/10">
                          <motion.div
                            className="h-full rounded-full bg-blue"
                            initial={false}
                            animate={{ scaleX: Math.min(code.trim().length / 6, 1) }}
                            style={{ transformOrigin: 'left' }}
                            transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
                          />
                        </div>
                      </Field>

                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        loading={busy}
                        disabled={code.trim().length < 6}
                        icon={!busy && <ShieldCheck className="h-4 w-4" />}
                      >
                        {busy ? 'Verifying' : step === 'enroll' ? 'Activate 2FA' : 'Sign in'}
                      </Button>

                      <button
                        type="button"
                        onClick={restart}
                        className="mx-auto flex items-center gap-1.5 text-caption2 text-label4 transition-colors hover:text-label2"
                      >
                        <ArrowLeft className="h-3 w-3" />
                        Start over
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Card>

            <motion.p
              {...rise(0.5)}
              className="mt-5 text-center text-caption2 text-label4"
            >
              Access is logged. Unauthorised use is a breach of platform policy.
            </motion.p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
